import React, { createContext, FC, useReducer } from 'react';

import { getLogger } from '@packages/core/logging';
import {
    notificationsActions,
    initialState,
    NotificationsActionsDispatcher,
    notificationsReducer,
    NotificationsState,
} from './notifications.state';
import { NotificationsService } from './notifications.service';

const logger = getLogger('notifications');

export const NotificationsStateContext = createContext<NotificationsState | undefined>(undefined);
export const NotificationsDispatchContext = createContext<NotificationsActionsDispatcher | undefined>(undefined);

interface NotificationsProviderProps {
    children: React.ReactNode;
}

export const NotificationsProvider: FC<NotificationsProviderProps> = ({ children }) => {
    const [state, dispatch] = useReducer(notificationsReducer, initialState);

    return (
        <NotificationsStateContext.Provider value={state}>
            <NotificationsDispatchContext.Provider value={dispatch}>{children}</NotificationsDispatchContext.Provider>
        </NotificationsStateContext.Provider>
    );
};

interface NotificationsDataAccess {
    notificationsService: NotificationsService;
    notificationsDispatch: NotificationsActionsDispatcher;
    ignoreCache?: boolean;
    signal?: AbortSignal;
}

export async function fetchNotificationsSummary({
    notificationsService,
    notificationsDispatch,
    signal,
    ignoreCache,
}: NotificationsDataAccess) {
    try {
        const response = await notificationsService.fetchNotificationsSummary(signal, ignoreCache);

        if (!response.success) {
            return Promise.reject(response.data);
        }

        notificationsDispatch(
            notificationsActions.updateNotificationSummary({
                notificationSummary: response.data,
            })
        );
        return response.data;
    } catch (e) {
        logger.error('Failed to fetch the notifications summary', { error: e });
    }
}

export async function fetchNotificationsList({
    notificationsService,
    notificationsDispatch,
    signal,
    ignoreCache,
}: NotificationsDataAccess) {
    const response = await notificationsService.fetchNotifications(signal, ignoreCache);

    if (!response.success) {
        return Promise.reject(response.data);
    }
    notificationsDispatch(
        notificationsActions.updateNotifications({
            notifications: response.data.notifications,
        })
    );
    return response.data;
}
